import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import { 
  User, 
  Mail, 
  Briefcase, 
  Star, 
  Trash2,
  Save,
  Heart,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { API_URL } from '../config';

export default function ProfilePage() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [role, setRole] = useState(user?.role || 'medecin');
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name);
      setRole(user.role);
      loadFavorites();
    }
  }, [user]);

  const loadFavorites = async () => {
    try {
      const response = await fetch(`${API_URL}/api/favorites`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setFavorites(data.favorites || []);
      }
    } catch (error) {
      console.error('Erreur lors du chargement des favoris:', error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    
    try {
      const response = await fetch(`${API_URL}/api/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ name, role })
      });

      if (response.ok) {
        toast.success('Profil mis à jour avec succès');
        // Refresh user data
        const meResponse = await fetch(`${API_URL}/api/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (meResponse.ok) {
          const data = await meResponse.json();
          // Update user context if needed
        }
      } else {
        throw new Error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du profil');
    } finally {
      setSavingProfile(false);
    }
  };

  const removeFavorite = async (itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/favorites/${itemId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        setFavorites(favorites.filter(fav => fav.id !== itemId));
        toast.success('Favori supprimé');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const getRoleLabel = (roleValue) => {
    const roles = {
      'medecin': 'Médecin',
      'infirmier': 'Infirmier(ère)',
      'interne': 'Interne'
    };
    return roles[roleValue] || roleValue;
  };

  const getTypeIcon = (type) => {
    switch(type) {
      case 'medication':
        return '💊';
      case 'protocol':
        return '📋';
      case 'tool':
        return '🔧';
      default:
        return '⭐';
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--bg))] text-[hsl(var(--text))] p-4 md:p-6" data-testid="profile-page">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="flex items-center justify-center gap-3">
            <User className="w-10 h-10 text-cyan-400" />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-cyan-400 to-blue-300 bg-clip-text text-transparent">
              Mon Profil
            </h1>
          </div>
          <p className="text-[hsl(var(--text-dim))] text-sm">
            Gérez vos informations et vos favoris
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Informations du profil */}
          <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <User className="w-5 h-5" />
                Informations personnelles
              </CardTitle>
              <CardDescription className="text-[hsl(var(--text-dim))]">
                Modifiez vos informations de profil
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                {/* Email (read-only) */}
                <div>
                  <Label htmlFor="email" className="flex items-center gap-2 text-sm text-[hsl(var(--text))]">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={user.email}
                    disabled
                    className="mt-1 bg-[hsl(var(--bg))] cursor-not-allowed"
                  />
                  <p className="text-xs text-[hsl(var(--text-dim))] mt-1">
                    L'email ne peut pas être modifié
                  </p>
                </div>

                {/* Name */}
                <div>
                  <Label htmlFor="name" className="flex items-center gap-2 text-sm text-[hsl(var(--text))]">
                    <User className="w-4 h-4" />
                    Nom complet
                  </Label>
                  <Input
                    id="name"
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="mt-1"
                    data-testid="profile-name-input"
                  />
                </div>

                {/* Role */}
                <div>
                  <Label htmlFor="role" className="flex items-center gap-2 text-sm text-[hsl(var(--text))]">
                    <Briefcase className="w-4 h-4" />
                    Profession
                  </Label>
                  <select
                    id="role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                    className="mt-1 w-full px-3 py-2 bg-[hsl(var(--bg))] border border-[hsl(var(--border))] rounded-lg text-[hsl(var(--text))] focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                    data-testid="profile-role-select"
                  >
                    <option value="medecin">Médecin</option>
                    <option value="infirmier">Infirmier(ère)</option>
                    <option value="interne">Interne</option>
                  </select>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-cyan-500 hover:bg-cyan-600 text-white"
                  disabled={savingProfile}
                  data-testid="save-profile-button"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingProfile ? 'Enregistrement...' : 'Enregistrer les modifications'}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Statistiques */}
          <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-cyan-300">
                <Star className="w-5 h-5" />
                Statistiques
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="p-4 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--text-dim))]">Membre depuis</p>
                    <p className="text-lg font-bold text-cyan-300">
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('fr-FR') : 'N/A'}
                    </p>
                  </div>
                  <User className="w-8 h-8 text-cyan-400" />
                </div>
              </div>

              <div className="p-4 rounded-lg bg-blue-500/10 border border-blue-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--text-dim))]">Favoris enregistrés</p>
                    <p className="text-lg font-bold text-blue-300">
                      {favorites.length}
                    </p>
                  </div>
                  <Heart className="w-8 h-8 text-blue-400" />
                </div>
              </div>

              <div className="p-4 rounded-lg bg-purple-500/10 border border-purple-500/30">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-[hsl(var(--text-dim))]">Rôle</p>
                    <Badge className="bg-purple-500 text-white mt-1">
                      {getRoleLabel(user.role)}
                    </Badge>
                  </div>
                  <Briefcase className="w-8 h-8 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Favoris */}
        <Card className="bg-[hsl(var(--bg-elev))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-cyan-300">
              <Heart className="w-5 h-5" />
              Mes Favoris ({favorites.length})
            </CardTitle>
            <CardDescription className="text-[hsl(var(--text-dim))]">
              Accédez rapidement à vos outils et protocoles favoris
            </CardDescription>
          </CardHeader>
          <CardContent>
            {favorites.length === 0 ? (
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <Star className="w-4 h-4 text-blue-400" />
                <AlertDescription className="text-[hsl(var(--text))]">
                  Vous n'avez pas encore de favoris. Ajoutez des médicaments, protocoles ou outils en cliquant sur l'icône ⭐ sur leurs pages respectives.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="grid md:grid-cols-2 gap-4">
                {favorites.map((fav, index) => (
                  <div 
                    key={index}
                    className="p-4 rounded-lg bg-[hsl(var(--bg))] border border-[hsl(var(--border))] hover:border-cyan-500/50 transition-all group"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-2xl">{getTypeIcon(fav.type)}</span>
                          <Badge className="bg-cyan-500/20 text-cyan-300 text-xs">
                            {fav.type}
                          </Badge>
                        </div>
                        <h3 className="font-semibold text-[hsl(var(--text))] mb-1">
                          {fav.name}
                        </h3>
                        <p className="text-xs text-[hsl(var(--text-dim))]">
                          Ajouté le {new Date(fav.added_at).toLocaleDateString('fr-FR')}
                        </p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(fav.path)}
                          className="text-cyan-400 hover:text-cyan-300"
                          data-testid={`open-favorite-${fav.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFavorite(fav.id)}
                          className="text-red-400 hover:text-red-300"
                          data-testid={`remove-favorite-${fav.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
